import type {
	HookStateFunction,
	OverrideContainerComponent,
	StoreActionApi,
} from 'react-sweet-state';
import {
	createStore,
	createStateHook,
	createActionsHook,
	createContainer,
} from 'react-sweet-state';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import type { BulkCreateErrorType } from '../ui/bulk-work-create-common/BulkCreateErrorSectionMessage.tsx';

export type AiBulkNavigationPageType = 'default' | 'configure' | 'edit_issue';

type NavigationState = {
	currentPage: AiBulkNavigationPageType;
	flags?: FlagsStateContainer;
	errorMessage: BulkCreateErrorType;
};

const initialState: NavigationState = {
	currentPage: 'default',
	errorMessage: null,
};

const actions = {
	goToPage:
		(page: AiBulkNavigationPageType) =>
		({ setState }: StoreActionApi<NavigationState>) => {
			setState({ currentPage: page });
		},
	setFlags:
		(flags: FlagsStateContainer) =>
		({ setState }: StoreActionApi<NavigationState>) => {
			setState({ flags });
		},
	setErrorMessage:
		(errorMessage: BulkCreateErrorType) =>
		({ setState }: StoreActionApi<NavigationState>) => {
			setState({ errorMessage });
		},
};

const Store = createStore<NavigationState, typeof actions>({
	initialState,
	actions,
	name: 'AiBulkNavigationState',
});

export const AiBulkNavigationContextContainer: OverrideContainerComponent<unknown> =
	createContainer(Store);

const useActions = createActionsHook(Store);

export const useAiBulkNavigationGotoPage = (): ((page: AiBulkNavigationPageType) => void) => {
	const { goToPage } = useActions();
	return goToPage;
};

export const useAiBulkNavigationCurrentPage: HookStateFunction<AiBulkNavigationPageType, void> =
	createStateHook(Store, {
		selector: (state: NavigationState) => state.currentPage,
	});

export const useSetBulkCreateFlagsContainer = (): ((flags: FlagsStateContainer) => void) => {
	const { setFlags } = useActions();
	return setFlags;
};

export const useBulkCreateFlagsContainer: HookStateFunction<FlagsStateContainer | undefined, void> =
	createStateHook(Store, {
		selector: (state: NavigationState) => state.flags,
	});

export const useSetErrorMessage = (): ((errorMessage: BulkCreateErrorType) => void) => {
	const { setErrorMessage } = useActions();
	return setErrorMessage;
};

export const useErrorMessage: HookStateFunction<BulkCreateErrorType, void> = createStateHook(
	Store,
	{
		selector: (state: NavigationState) => state.errorMessage,
	},
);
